import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, XCircle, Clock, EyeOff, AlertTriangle, AlertCircle,
  ExternalLink, RefreshCw, Settings, Wallet, ShieldCheck, CreditCard,
  TrendingUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Centrale dark Powersure surface — zelfde gradient als Action Center top
const DARK_SURFACE_STYLE = {
  background:
    'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)',
};
const DARK_SURFACE_CLS =
  'rounded-2xl border border-white/10 p-5 shadow-2xl text-white';
const DARK_INPUT_CLS =
  'block w-full text-sm bg-white/5 border border-white/15 rounded-md px-2.5 py-1.5 text-white placeholder-white/35 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/50';
const DARK_BTN_PRIMARY =
  'text-xs px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-md font-semibold transition-colors';
const DARK_BTN_SECONDARY =
  'text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/15 rounded-md text-white/85 transition-colors';
const DARK_BTN_GHOST =
  'text-xs px-3 py-1.5 text-white/55 hover:text-emerald-300 underline transition-colors';

const PRIO_TONE = {
  P0: 'bg-rose-500/10 border-rose-400/40 text-rose-100',
  P1: 'bg-amber-500/10 border-amber-400/30 text-amber-100',
  P2: 'bg-white/5 border-white/15 text-white/80',
};

const STATUS_LABEL = {
  open: 'Open', approved: 'Goedgekeurd', rejected: 'Afgekeurd',
  later: 'Later', ignored: 'Genegeerd', resolved: 'Opgelost',
};

const STATUS_TONE = {
  open: 'bg-white/10 text-white/85 border border-white/15',
  approved: 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/30',
  rejected: 'bg-rose-500/15 text-rose-300 border border-rose-400/30',
  later: 'bg-amber-500/15 text-amber-300 border border-amber-400/30',
  ignored: 'bg-white/8 text-white/65 border border-white/15',
  resolved: 'bg-sky-500/15 text-sky-300 border border-sky-400/30',
};

const PRIO_BADGE_TONE = {
  P0: 'bg-rose-500/20 text-rose-200 border border-rose-400/40',
  P1: 'bg-amber-500/20 text-amber-200 border border-amber-400/40',
  P2: 'bg-white/10 text-white/80 border border-white/15',
};

const BLOK_TONE = {
  green: 'bg-emerald-500', orange: 'bg-amber-500',
  red: 'bg-rose-500', grey: 'bg-slate-300',
};

function fmtEUR(v) {
  if (v == null) return '—';
  try {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency', currency: 'EUR',
    }).format(v);
  } catch { return `€ ${v}`; }
}

function ActionCard({ item, token, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const decide = async (decision) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/owner-action-items/${item.id}/decision`,
        { decision },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(STATUS_LABEL[decision]);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || 'Mislukt');
    } finally { setBusy(false); }
  };

  const hasTech = item.technical_details
                  && Object.keys(item.technical_details).length > 0;

  return (
    <div
      className={`border rounded-2xl p-4 backdrop-blur-sm ${PRIO_TONE[item.priority] || ''}`}
      data-testid={`action-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PRIO_BADGE_TONE[item.priority] || ''}`}>
              {item.priority}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-white/55 font-medium">
              {item.category}
            </span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_TONE[item.status] || ''}`}>
              {STATUS_LABEL[item.status] || item.status}
            </span>
          </div>
          <div className="font-semibold mt-1.5 text-white text-[15px] leading-tight" style={{ fontFamily: 'Outfit' }}>{item.title}</div>
          {item.description && (
            <div className="text-xs text-white/70 mt-1 leading-relaxed">{item.description}</div>
          )}
          {item.recommended_action && (
            <div className="text-xs text-emerald-300/90 mt-1.5 italic leading-snug">
              → {item.recommended_action}
            </div>
          )}
          {item.external_action_blocked && (
            <div className="text-[10px] mt-1.5 inline-flex items-center gap-1 text-amber-200">
              <AlertTriangle className="w-3 h-3" /> Live GO door owner nodig
            </div>
          )}
          {hasTech && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                className="text-[10px] text-white/55 hover:text-emerald-300 underline transition-colors"
                data-testid={`action-toggle-tech-${item.id}`}
              >
                {showDetails ? 'Technische details verbergen'
                             : 'Technische details tonen'}
              </button>
              {showDetails && (
                <pre
                  className="mt-1 text-[10px] bg-black/40 border border-white/10 text-white/70 rounded p-1.5 overflow-auto font-mono"
                  data-testid={`action-tech-${item.id}`}
                >
                  {JSON.stringify(item.technical_details, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
      {item.status === 'open' && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          <button disabled={busy} onClick={() => decide('approved')}
            className="text-xs px-2.5 py-1 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded font-semibold inline-flex items-center gap-1 transition-colors"
            data-testid={`action-decide-approved-${item.id}`}>
            <CheckCircle2 className="w-3 h-3" /> Goedkeuren
          </button>
          <button disabled={busy} onClick={() => decide('rejected')}
            className="text-xs px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-rose-400/30 text-rose-300 rounded inline-flex items-center gap-1 transition-colors"
            data-testid={`action-decide-rejected-${item.id}`}>
            <XCircle className="w-3 h-3" /> Afkeuren
          </button>
          <button disabled={busy} onClick={() => decide('later')}
            className="text-xs px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-amber-400/30 text-amber-300 rounded inline-flex items-center gap-1 transition-colors"
            data-testid={`action-decide-later-${item.id}`}>
            <Clock className="w-3 h-3" /> Later
          </button>
          <button disabled={busy} onClick={() => decide('ignored')}
            className="text-xs px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/15 text-white/65 rounded inline-flex items-center gap-1 transition-colors"
            data-testid={`action-decide-ignored-${item.id}`}>
            <EyeOff className="w-3 h-3" /> Negeren
          </button>
        </div>
      )}
    </div>
  );
}

function OwnerActionCenter({ token }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/owner-action-center`,
        { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch (e) { toast.error('Action Center kon niet laden'); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const items = (data?.items || []).filter(i => {
    if (filter === 'all') return true;
    if (filter === 'open') return i.status === 'open';
    if (filter === 'P0') return i.priority === 'P0';
    return i.category === filter;
  });

  return (
    <div
      className="rounded-2xl border border-white/10 p-5 space-y-3 shadow-2xl"
      style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
      data-testid="owner-action-center"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2 text-white" style={{ fontFamily: 'Outfit' }}>
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
              <AlertCircle className="w-4 h-4" />
            </span>
            Owner Action Center
          </h2>
          <div className="text-xs text-white/55 mt-1 ml-11">
            <span className="text-rose-300 font-medium">{data?.counts?.P0 ?? 0}</span> P0 ·
            <span className="text-amber-300 font-medium"> {data?.counts?.P1 ?? 0}</span> P1 ·
            <span className="text-white/65"> {data?.counts?.P2 ?? 0}</span> P2 ·
            <span className="text-emerald-300 font-medium"> {data?.counts?.open ?? 0}</span> Open
          </div>
        </div>
        <button onClick={load} disabled={loading}
          className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/8 hover:bg-white/15 border border-white/15 rounded-full text-white/85 transition-colors">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Vernieuwen
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="action-center-filters">
        {['open', 'all', 'P0', 'betaling', 'offerte', 'mail', 'ads', 'pilot', 'app', 'systeem'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
              filter === f
                ? 'bg-emerald-500 text-emerald-950 border-emerald-400 font-semibold'
                : 'bg-white/5 border-white/15 text-white/75 hover:bg-white/10'}`}
            data-testid={`action-center-filter-${f}`}>
            {f === 'open' ? 'Wacht op mij' : f === 'all' ? 'Alle' : f}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.length === 0 && (
          <div className="text-sm text-white/55 italic col-span-full text-center py-4">
            Niets dat nu uw aandacht nodig heeft — alles veilig.
          </div>
        )}
        {items.map(it => (
          <ActionCard key={it.id} item={it} token={token} onChanged={load} />
        ))}
      </div>
    </div>
  );
}

function BusinessFinanceCard({ token }) {
  const [s, setS] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API}/admin/business-finance-summary`,
          { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/business-cost-settings`,
          { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setS(r.data); setSettings(c.data);
    } catch (e) { /* */ }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/business-cost-settings`, {
        monthly_tool_costs_estimate: Number(settings.monthly_tool_costs_estimate) || 0,
        ads_spend_manual_estimate: Number(settings.ads_spend_manual_estimate) || 0,
        resend_cost_estimate: Number(settings.resend_cost_estimate) || 0,
        hosting_cost_estimate: Number(settings.hosting_cost_estimate) || 0,
        other_costs_estimate: Number(settings.other_costs_estimate) || 0,
        note: settings.note || '',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kosten opgeslagen');
      setShowSettings(false);
      await load();
    } catch { toast.error('Opslaan mislukt'); }
    finally { setBusy(false); }
  };

  if (!s) return null;
  return (
    <div className={DARK_SURFACE_CLS} style={DARK_SURFACE_STYLE}
      data-testid="finance-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            <Wallet className="w-4 h-4" />
          </span>
          <div>
            <h2 className="text-base font-semibold text-white leading-tight" style={{ fontFamily: 'Outfit' }}>Deze maand</h2>
            <p className="text-[11px] text-white/55 mt-0.5">Omzet, kosten en resultaat</p>
          </div>
        </div>
        <button onClick={() => setShowSettings(v => !v)}
          className={DARK_BTN_SECONDARY + ' inline-flex items-center gap-1.5'}
          data-testid="finance-toggle-settings">
          <Settings className="w-3 h-3" /> Kosten bewerken
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-4">
        <div data-testid="finance-revenue" className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Omzet</div>
          <div className="text-lg font-semibold text-white mt-0.5" style={{ fontFamily: 'Outfit' }}>{fmtEUR(s.revenue_eur)}</div>
        </div>
        <div data-testid="finance-costs" className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Kosten totaal</div>
          <div className="text-lg font-semibold text-white mt-0.5" style={{ fontFamily: 'Outfit' }}>{fmtEUR(s.costs?.total)}</div>
        </div>
        <div data-testid="finance-result" className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Resultaat</div>
          <div className={`text-lg font-semibold mt-0.5 ${s.result_eur >= 0 ? 'text-emerald-300' : 'text-rose-300'}`} style={{ fontFamily: 'Outfit' }}>
            {fmtEUR(s.result_eur)}
          </div>
        </div>
        <div data-testid="finance-margin" className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Marge</div>
          <div className="text-lg font-semibold text-white mt-0.5" style={{ fontFamily: 'Outfit' }}>{s.margin_pct == null ? '—' : `${s.margin_pct}%`}</div>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Betalende klanten</div>
          <div className="text-base font-semibold text-white mt-0.5">{s.paying_customers}</div>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Nieuwe leads</div>
          <div className="text-base font-semibold text-white mt-0.5">{s.new_leads}</div>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Kosten per lead</div>
          <div className="text-base font-semibold text-white mt-0.5">{s.cost_per_lead_eur == null ? '—' : fmtEUR(s.cost_per_lead_eur)}</div>
        </div>
        <div className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Break-even klanten</div>
          <div className="text-base font-semibold text-white mt-0.5">{s.break_even_customers == null ? '—' : s.break_even_customers}</div>
        </div>
      </div>
      {(s.notes || []).length > 0 && (
        <div className="text-[11px] text-amber-300/90 space-y-0.5 mt-3">
          {s.notes.map((n, i) => <div key={i}>• {n}</div>)}
        </div>
      )}
      {showSettings && (
        <div className="border-t border-white/10 pt-3 space-y-2 mt-3" data-testid="finance-settings">
          {[
            { key: 'ads_spend_manual_estimate', label: 'Ads-budget (handmatig)' },
            { key: 'monthly_tool_costs_estimate', label: 'Maandelijkse tools' },
            { key: 'resend_cost_estimate', label: 'E-mail (Resend)' },
            { key: 'hosting_cost_estimate', label: 'Hosting' },
            { key: 'other_costs_estimate', label: 'Overige kosten' },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 text-xs text-white/80">
              <span className="w-44 text-white/65">{label}</span>
              <input type="number" step="0.01" min="0"
                value={settings[key] || ''}
                onChange={e => setSettings(p => ({ ...p, [key]: e.target.value }))}
                className={DARK_INPUT_CLS + ' max-w-[140px]'}
                data-testid={`finance-setting-${key}`} />
              <span className="text-white/55">€</span>
            </label>
          ))}
          <button onClick={saveSettings} disabled={busy}
            className={DARK_BTN_PRIMARY}
            data-testid="finance-settings-save">
            {busy ? 'Bezig…' : 'Opslaan'}
          </button>
        </div>
      )}
    </div>
  );
}

function BlokStatusCard({ token }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/blok-status`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => {});
  }, [token]);

  if (!data) return null;
  return (
    <div className={DARK_SURFACE_CLS} style={DARK_SURFACE_STYLE}
      data-testid="blok-status-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
          <ShieldCheck className="w-4 h-4" />
        </span>
        <div>
          <h2 className="text-base font-semibold text-white leading-tight" style={{ fontFamily: 'Outfit' }}>Powersure status</h2>
          <p className="text-[11px] text-white/55 mt-0.5">Per blok in één oogopslag</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
        {data.blokken.map(b => (
          <div key={b.id}
            className="rounded-xl bg-white/5 border border-white/10 p-2.5 text-xs"
            data-testid={`blok-status-${b.id}`}>
            <div className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${BLOK_TONE[b.color] || 'bg-slate-300'}`} />
              <span className="font-semibold text-white">{b.label}</span>
            </div>
            <div className="text-[11px] text-white/65 mt-1 leading-snug">{b.summary}</div>
            {b.owner_needed && (
              <div className="text-[10px] text-rose-300 mt-1 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Owner-actie nodig
              </div>
            )}
          </div>
        ))}
      </div>
      <a href="/admin?tab=pilot_devices"
        className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 transition-colors"
        data-testid="blok-status-open-customer-app">
        <ExternalLink className="w-3 h-3" /> Bekijk klant-app als testklant
      </a>
    </div>
  );
}


// ============== Ads Budget Guard ============================================
function AdsBudgetGuardCard({ token }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ monthly_budget_limit: 0,
                                     current_month_spend_manual: 0,
                                     hard_stop_enabled: true,
                                     approval_required_above_limit: true,
                                     note: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, st] = await Promise.all([
        axios.get(`${API}/admin/ads-budget-settings`,
          { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/ads-budget-status`,
          { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData({ settings: s.data, status: st.data });
      setForm({
        monthly_budget_limit: s.data.monthly_budget_limit,
        current_month_spend_manual: s.data.current_month_spend_manual,
        hard_stop_enabled: s.data.hard_stop_enabled,
        approval_required_above_limit: s.data.approval_required_above_limit,
        note: s.data.note || '',
      });
    } catch { /* swallow — admin sees stale view */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/ads-budget-settings`,
        { ...form, currency: 'EUR', current_month_spend_source: 'manual' },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Budget opgeslagen');
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt');
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const g = data.status.guard;
  const st = data.status;
  const STATE_TONE = {
    safe: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
    warning: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
    critical: 'bg-orange-500/15 text-orange-300 border-orange-400/30',
    stop: 'bg-rose-500/15 text-rose-300 border-rose-400/30',
    no_limit_set: 'bg-white/8 text-white/65 border-white/15',
  };
  const eur = (n) => n == null ? '—' :
    new Intl.NumberFormat('nl-NL',
      { style: 'currency', currency: 'EUR' }).format(n);

  return (
    <div className={DARK_SURFACE_CLS} style={DARK_SURFACE_STYLE}
      data-testid="ads-budget-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            <TrendingUp className="w-4 h-4" />
          </span>
          <div>
            <h2 className="text-base font-semibold text-white leading-tight" style={{ fontFamily: 'Outfit' }}>Ads budget guard</h2>
            <p className="text-[11px] text-white/55 mt-0.5">Maandbudget en projectie</p>
          </div>
        </div>
        <span className={`text-[10.5px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full border ${STATE_TONE[g.state]}`}
          data-testid="ads-budget-state">{g.label}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
        <div data-testid="ads-budget-limit" className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Maxbudget</div>
          <div className="text-lg font-semibold text-white mt-0.5" style={{ fontFamily: 'Outfit' }}>{eur(st.monthly_budget_limit_eur)}</div>
        </div>
        <div data-testid="ads-budget-spend" className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Uitgegeven</div>
          <div className="text-lg font-semibold text-white mt-0.5" style={{ fontFamily: 'Outfit' }}>{eur(st.current_spend_eur)}</div>
        </div>
        <div data-testid="ads-budget-remaining" className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Resterend</div>
          <div className="text-lg font-semibold text-emerald-300 mt-0.5" style={{ fontFamily: 'Outfit' }}>{eur(st.remaining_eur)}</div>
        </div>
        <div data-testid="ads-budget-runrate" className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Projectie maand</div>
          <div className="text-lg font-semibold text-white mt-0.5" style={{ fontFamily: 'Outfit' }}>{eur(st.projected_full_month_eur)}</div>
        </div>
      </div>
      {st.utilization_pct != null && (
        <div className="h-2 w-full bg-white/8 rounded-full mb-2 overflow-hidden">
          <div
            className={`h-2 rounded-full ${
              g.state === 'stop' ? 'bg-rose-400' :
              g.state === 'critical' ? 'bg-orange-400' :
              g.state === 'warning' ? 'bg-amber-400' : 'bg-emerald-400'}`}
            style={{ width: `${Math.min(100, st.utilization_pct)}%` }}
            data-testid="ads-budget-progress" />
        </div>
      )}
      <div className="text-xs text-white/70 mb-1.5">{g.reason}</div>
      <div className="text-[11px] text-white/50 mb-3">
        Ads-agent mutaties:&nbsp;
        <span className={g.mutations_allowed ? 'text-emerald-300' : 'text-rose-300'}>
          {g.mutations_allowed ? 'toegestaan' : 'geblokkeerd'}
        </span>
      </div>
      {!editing ? (
        <button
          onClick={() => setEditing(true)}
          className={DARK_BTN_SECONDARY}
          data-testid="ads-budget-edit-btn">
          Budget aanpassen
        </button>
      ) : (
        <div className="border-t border-white/10 pt-3 mt-1 space-y-2"
          data-testid="ads-budget-edit-form">
          <label className="block text-xs text-white/75">Maxbudget per maand (€)
            <input type="number" step="0.01" min="0"
              value={form.monthly_budget_limit}
              onChange={(e) => setForm(f => ({ ...f,
                monthly_budget_limit: Number(e.target.value) }))}
              className={DARK_INPUT_CLS + ' mt-1'}
              data-testid="ads-budget-input-limit" />
          </label>
          <label className="block text-xs text-white/75">Uitgegeven deze maand (handmatig, €)
            <input type="number" step="0.01" min="0"
              value={form.current_month_spend_manual}
              onChange={(e) => setForm(f => ({ ...f,
                current_month_spend_manual: Number(e.target.value) }))}
              className={DARK_INPUT_CLS + ' mt-1'}
              data-testid="ads-budget-input-spend" />
          </label>
          <label className="flex items-center gap-2 text-xs text-white/75">
            <input type="checkbox" checked={form.hard_stop_enabled}
              onChange={(e) => setForm(f => ({ ...f,
                hard_stop_enabled: e.target.checked }))}
              className="accent-emerald-500"
              data-testid="ads-budget-hard-stop" />
            Hard-stop bij 100% (Ads-agent mag niet boven)
          </label>
          <label className="flex items-center gap-2 text-xs text-white/75">
            <input type="checkbox" checked={form.approval_required_above_limit}
              onChange={(e) => setForm(f => ({ ...f,
                approval_required_above_limit: e.target.checked }))}
              className="accent-emerald-500"
              data-testid="ads-budget-approval" />
            Owner-akkoord vereist bij budgetverhoging
          </label>
          <label className="block text-xs text-white/75">Notitie
            <input type="text" value={form.note}
              onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
              className={DARK_INPUT_CLS + ' mt-1'}
              data-testid="ads-budget-input-note" />
          </label>
          <div className="flex gap-1.5 pt-1">
            <button onClick={save} disabled={busy}
              className={DARK_BTN_PRIMARY}
              data-testid="ads-budget-save-btn">
              {busy ? 'Bezig…' : 'Opslaan'}
            </button>
            <button onClick={() => setEditing(false)}
              className={DARK_BTN_SECONDARY}
              data-testid="ads-budget-cancel-btn">
              Later
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ============== Stripe Prices Admin Config ==================================
function StripePricesCard({ token }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ intro_price_id: '',
                                     regular_price_id: '',
                                     note: '' });
  const [showTech, setShowTech] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/admin/payment-config/stripe-prices`,
          { headers: { Authorization: `Bearer ${token}` } }),
        axios.post(`${API}/admin/payment-config/stripe-prices/test`, {},
          { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData({ config: s.data, test: t.data });
      setForm({
        intro_price_id: s.data.intro_price_id || '',
        regular_price_id: s.data.regular_price_id || '',
        note: s.data.note || '',
      });
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (form.intro_price_id === form.regular_price_id) {
      toast.error('Intro en reguliere Price ID moeten verschillend zijn.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/payment-config/stripe-prices`,
        { ...form, is_active: true },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Stripe abonnementsprijzen opgeslagen');
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                  || e?.response?.data?.detail || 'Opslaan mislukt');
    } finally { setBusy(false); }
  };

  const runTest = async () => {
    try {
      const r = await axios.post(
        `${API}/admin/payment-config/stripe-prices/test`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setTestResult(r.data);
      setShowTech(true);
      const v = r.data.stripe_validation || {};
      if (!r.data.complete) {
        toast.error('Configuratie incompleet — vul ontbrekende Price IDs');
      } else if (v.performed === false) {
        toast.warning?.(v.message || 'Lokaal compleet, Stripe-validatie niet uitgevoerd')
          ?? toast(v.message || 'Lokaal compleet, Stripe-validatie niet uitgevoerd');
      } else if (v.performed && v.all_valid) {
        toast.success('Configuratie compleet + Stripe valideerde alle Price IDs ✓');
      } else if (v.performed && !v.all_valid) {
        toast.error('Stripe gaf afwijkingen — bekijk technische details');
      } else {
        toast.success('Configuratie compleet ✓');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test mislukt');
    }
  };

  const autoDiscover = async (apply) => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/payment-config/stripe-prices/auto-discover`,
        {}, { params: { apply: apply ? 'true' : 'false' },
              headers: { Authorization: `Bearer ${token}` } });
      setTestResult({ auto_discovery: r.data, stripe_validation: null });
      setShowTech(true);
      if (r.data.auto_applied) {
        toast.success('Stripe Price IDs automatisch gevonden + opgeslagen ✓');
        await load();
      } else if (r.data.can_auto_apply) {
        toast(`Topkandidaten klaar — klik "Toepassen & opslaan" om te bevestigen`);
      } else if (r.data.intro_candidates?.length || r.data.regular_candidates?.length) {
        toast('Meerdere kandidaten — controleer in technische details');
      } else {
        toast.error('Geen Stripe Prices gevonden — handmatig invullen');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                  || e?.response?.data?.detail
                  || 'Auto-discovery mislukt');
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const t = data.test;
  const STATE_TONE = t.complete
    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30'
    : 'bg-amber-500/15 text-amber-300 border-amber-400/30';
  const stateLabel = t.complete ? 'Klaar' : 'Niet gekoppeld';

  const SourceTag = ({ which }) => {
    const info = t[which];
    if (!info?.set) return <span className="text-amber-300 font-medium">Niet ingesteld</span>;
    const label = info.source === 'env' ? 'Vast' : 'Admin';
    return <span className="text-emerald-300">Ingesteld · {label}</span>;
  };

  return (
    <div className={DARK_SURFACE_CLS} style={DARK_SURFACE_STYLE}
      data-testid="stripe-prices-card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            <CreditCard className="w-4 h-4" />
          </span>
          <div>
            <h2 className="text-base font-semibold text-white leading-tight" style={{ fontFamily: 'Outfit' }}>Stripe abonnementsprijzen</h2>
            <p className="text-[11px] text-white/55 mt-0.5">€9,95 eerste 3 maanden · daarna €14,95</p>
          </div>
        </div>
        <span className={`text-[10.5px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full border ${STATE_TONE}`}
          data-testid="stripe-prices-state">
          {stateLabel}
        </span>
      </div>
      <p className="text-xs text-white/65 mb-3 leading-relaxed">
        Kaartbetaling werkt op productie. iDEAL voor abonnement wacht op
        owner-activatie (Stripe Dashboard + env-flag). Preview-omgeving heeft
        soms geen live prijzen — dat is geen productie-issue.
      </p>
      {testResult?.stripe_validation && (
        <div
          className={`text-[11px] mb-3 px-2.5 py-1.5 rounded-md border ${
            testResult.stripe_validation.performed
              && testResult.stripe_validation.all_valid
                ? 'bg-emerald-500/10 border-emerald-400/30 text-emerald-200'
                : testResult.stripe_validation.performed
                  ? 'bg-rose-500/10 border-rose-400/30 text-rose-200'
                  : 'bg-amber-500/10 border-amber-400/30 text-amber-200'}`}
          data-testid="stripe-validation-banner">
          <span className="font-semibold">
            {testResult.stripe_validation.performed
              ? (testResult.stripe_validation.all_valid
                  ? 'Stripe: gevalideerd ✓'
                  : 'Stripe: afwijkingen gevonden')
              : 'Stripe: niet gevalideerd'}
          </span>
          {' · '}
          {testResult.stripe_validation.message}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-3">
        <div data-testid="stripe-price-intro-row" className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Intro · €9,95</div>
          <div className="font-semibold mt-0.5"><SourceTag which="intro" /></div>
          {t.intro?.masked && (
            <div className="text-[10px] text-white/40 font-mono mt-0.5">
              {t.intro.masked}
            </div>
          )}
        </div>
        <div data-testid="stripe-price-regular-row" className="rounded-xl bg-white/5 border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-white/55">Regulier · €14,95</div>
          <div className="font-semibold mt-0.5"><SourceTag which="regular" /></div>
          {t.regular?.masked && (
            <div className="text-[10px] text-white/40 font-mono mt-0.5">
              {t.regular.masked}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-1">
        {!editing && (
          <button onClick={() => setEditing(true)}
            className={DARK_BTN_PRIMARY}
            data-testid="stripe-prices-edit-btn">
            Price IDs invullen
          </button>
        )}
        <button onClick={() => autoDiscover(false)}
          disabled={busy}
          className={DARK_BTN_SECONDARY + ' disabled:opacity-50'}
          data-testid="stripe-prices-discover-btn">
          {busy ? 'Zoeken…' : 'Auto-vinden'}
        </button>
        <button onClick={() => autoDiscover(true)}
          disabled={busy}
          className={DARK_BTN_PRIMARY + ' disabled:opacity-50'}
          data-testid="stripe-prices-discover-apply-btn">
          Toepassen &amp; opslaan
        </button>
        <button onClick={runTest}
          className={DARK_BTN_SECONDARY}
          data-testid="stripe-prices-test-btn">
          Test configuratie
        </button>
        <button onClick={() => setShowTech(s => !s)}
          className={DARK_BTN_GHOST}
          data-testid="stripe-prices-tech-toggle">
          {showTech ? 'Technische details verbergen' : 'Technische details tonen'}
        </button>
      </div>
      {editing && (
        <div className="border-t border-white/10 pt-3 mt-2 space-y-2"
          data-testid="stripe-prices-edit-form">
          <label className="block text-xs text-white/75">Introductie Price ID (€9,95)
            <input type="text" placeholder="price_1Abc…"
              value={form.intro_price_id}
              onChange={(e) => setForm(f => ({ ...f,
                intro_price_id: e.target.value.trim() }))}
              className={DARK_INPUT_CLS + ' font-mono mt-1'}
              data-testid="stripe-prices-input-intro" />
          </label>
          <label className="block text-xs text-white/75">Reguliere Price ID (€14,95)
            <input type="text" placeholder="price_1Xyz…"
              value={form.regular_price_id}
              onChange={(e) => setForm(f => ({ ...f,
                regular_price_id: e.target.value.trim() }))}
              className={DARK_INPUT_CLS + ' font-mono mt-1'}
              data-testid="stripe-prices-input-regular" />
          </label>
          <label className="block text-xs text-white/75">Notitie (optioneel)
            <input type="text" value={form.note}
              onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
              className={DARK_INPUT_CLS + ' mt-1'}
              data-testid="stripe-prices-input-note" />
          </label>
          <div className="flex gap-1.5 pt-1">
            <button onClick={save} disabled={busy}
              className={DARK_BTN_PRIMARY}
              data-testid="stripe-prices-save-btn">
              {busy ? 'Bezig…' : 'Opslaan'}
            </button>
            <button onClick={() => setEditing(false)}
              className={DARK_BTN_SECONDARY}
              data-testid="stripe-prices-cancel-btn">
              Later
            </button>
          </div>
        </div>
      )}
      {showTech && (
        <pre className="mt-3 text-[10px] bg-black/40 border border-white/10 text-white/70 rounded-md p-2.5 overflow-auto font-mono"
          data-testid="stripe-prices-tech">
{JSON.stringify({
  config_updated_at: data.config.updated_at,
  config_updated_by: data.config.updated_by,
  audit_log_count: data.config.audit_log_count,
  resolver_test: t,
  test_result_last: testResult,
}, null, 2)}
        </pre>
      )}
    </div>
  );
}


export default function OwnerActionCenterBlock({ token }) {
  return (
    <div className="space-y-3" data-testid="owner-action-center-block">
      <OwnerActionCenter token={token} />
      <BusinessFinanceCard token={token} />
      <StripePricesCard token={token} />
      <AdsBudgetGuardCard token={token} />
      <BlokStatusCard token={token} />
    </div>
  );
}
